import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { db } from "../firebase";
import { ref, onValue, set, remove, get } from "firebase/database";
import { PlusCircle, Edit, Trash2, X, 
  Thermometer, Droplets, Wind, Gauge, 
  Volume2, Sun } from "lucide-react";

const SENSOR_KEYS = {
  suhu: {label: "Suhu", unit: "°C", icon: <Thermometer className="w-6 h-6"/>},
  kelembapan: {label: "Kelembapan",  unit: "%", icon: <Droplets className="w-6 h-6"/>},
  co2: {label: "Gas CO₂",  unit: "ppm", icon: <Gauge className="w-6 h-6"/>},
  co: {label: "Asap",  unit: "ppm", icon: <Wind className="w-6 h-6"/>},
  kebisingan: {label: "Kebisingan",  unit: "dB", icon: <Volume2 className="w-6 h-6"/>},
  cahaya: {label: "Intensitas Cahaya",unit: "lux", icon: <Sun className="w-6 h-6"/>},
};

export default function KonfigurasiSistem() {
  const [deviceMap, setDeviceMap] = useState({});
  const [deviceStatus, setDeviceStatus] = useState({});
  const [sensorRealtime, setSensorRealtime] = useState({});
  const [ambangBatas, setAmbangBatas] = useState({});
  const [pilihSensor, setPilihSensor] = useState(null);
  const [editData, setEditData] = useState({});
  const [originalData, setOriginalData] = useState({});   //deteksi apakah ada perubahan
  const [toastAB, setToastAB] = useState(null);   //null - mengontrol notif simpan
  const [toastDK, setToastDK] = useState(null);
  const [toastH, setToastH] = useState(null);
  const [toastError, setToastError] = useState(null);
  const [kelasList, setKelasList] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({namaKelas: "", deviceId: ""});
  const [pilihDeviceId, setPilihDeviceId] = useState(null);
  const [showHapusModal, setShowHapusModal] = useState(false);
  const [hapusData, setHapusData] = useState(null);

  useEffect(() => {
    console.log("DEVICE MAP:", deviceMap);
  }, [deviceMap]);

  useEffect(() => {
    const ambangRef = ref(db, "ambangBatas");
    const sensorRef = ref(db, "dataHistoris");
    const mappingRef = ref(db, "deviceMapping");
    const statusRef = ref(db, "deviceStatus");

    onValue(ambangRef, (snapshot) => {
      if (snapshot.exists()) {
        setAmbangBatas(snapshot.val());
      }
    });

    onValue(sensorRef, (snapshot) => {
      if (snapshot.exists()) {
        setSensorRealtime(snapshot.val());
      }
    });

    onValue(mappingRef, (snap) => { setDeviceMap(snap.val() || {}); });
    onValue(statusRef, (snapshot) => { setDeviceStatus(snapshot.val() || {}); });
  }, []);


  const handleEdit = (sensorKey) => {
    setPilihSensor(sensorKey);
    const data = ambangBatas[sensorKey] || {};
    setEditData(data);
    setOriginalData(JSON.parse(JSON.stringify(data)));    //simpan salinan deep-copy sbg acuan data sebelum diubah
  };

  /**
   * Bandingkan editData dengan originalData secara rekursif (JSON stringify).
   * Tombol Simpan hanya aktif jika ada setidaknya satu field yang berbeda.
   */
  //const adaPerubahan = JSON.stringify(editData) !== JSON.stringify(originalData);

  const handleSave = async () => {
    if (!pilihSensor) return;
    const label = SENSOR_KEYS[pilihSensor]?.label ?? pilihSensor;
    await set(ref(db, `ambangBatas/${pilihSensor}`), editData);
    setPilihSensor(null);
    setToastAB({label});
    setTimeout(() => setToastAB(null), 5000);
  };

  // Render satu nilai ambang
  const renderNilaiAmbang = (val) => {
    if (val === undefined || val === null) return "-";
    // Jika object {min, max}
    if (typeof val === "object" && "min" in val && "max" in val) {
      return `${val.min} – ${val.max}`;
    }
    // Jika angka / string biasa
    return String(val);
  };

  /**
   * Tampilkan semua sub-level ambang batas sebuah sensor sebagai teks ringkas.
   * Firebase bisa menyimpan: peringatan, peringatan1, peringatan2, kritis, kritis1, dst.
   * Fungsi ini menampilkan semua yang ada tanpa hardcode nama field-nya.
   */
  const renderRingkasanLevel = (sensorValue, levelPrefix) => {
    if (!sensorValue) return "-";

    // Kumpulkan semua key yang diawali levelPrefix (mis. "waspada", "waspada batas bawah", ...)
    const entries = Object.entries(sensorValue).filter(([k]) =>
      k.startsWith(levelPrefix)
    );

    if (entries.length === 0) return "-";

    return (
      <div className="flex flex-col gap-1 text-xs">
        {entries.map(([k, v]) => {
          const suffix = k.replace(levelPrefix, ""); // "" / "batas bawah" / "batas atas"
          const label  = suffix ? `${levelPrefix} ${suffix}` : levelPrefix;
          
          return (
          <span key={k} className="flex justify-between">
            <span className="font-medium text-gray-500 shrink-0">{label}:</span>
            <span className="text-gray-700">{renderNilaiAmbang(v)}</span>
          </span>
          );
        })}
      </div>
    );
  };

  // form edit
  const renderEditField = (fieldKey) => {
    const val = editData[fieldKey];
    if (val === undefined || val === null) return null;

    // Nilai berupa object {min, max}
    if (typeof val === "object" && "min" in val && "max" in val) {
      return (
        <div key={fieldKey} className="space-y-1">
          <label className="block text-gray-600 text-sm font-medium capitalize">
            {fieldKey.replace(/(\d)/, " $1")} {/* "peringatan1" → "peringatan 1" */}
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-400 mb-0.5 block">Min</label>
              <input
                type="number"
                value={val.min ?? ""}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    [fieldKey]: { ...val, min: Number(e.target.value) },
                  })
                }
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-400 outline-none text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-400 mb-0.5 block">Max</label>
              <input
                type="number"
                value={val.max ?? ""}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    [fieldKey]: { ...val, max: Number(e.target.value) },
                  })
                }
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-400 outline-none text-sm"
              />
            </div>
          </div>
        </div>
      );
    }

    // Nilai berupa angka tunggal
    return (
      <div key={fieldKey}>
        <label className="block text-gray-600 text-sm mb-1 capitalize">
          {fieldKey.replace(/(\d)/, " $1")}
        </label>
        <input
          type="number"
          value={val ?? ""}
          onChange={(e) =>
            setEditData({ ...editData, [fieldKey]: Number(e.target.value) })
          }
          className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-400 outline-none text-sm"
        />
      </div>
    );
  };

  // tambah & edit kelas & deviceId
  const handleSubmit = async () => {
    const deviceIDBaru = form.deviceId.toUpperCase().trim();
    const kelasBaru = form.namaKelas.trim();

    if (!deviceIDBaru || !kelasBaru) {
        alert("Semua field wajib diisi");
        return;
    }

    try {
      // mode tambah
      if (!editMode) {
        // cek duplikasi kelas & id device
        if (deviceMap[deviceIDBaru]) {
          setToastError(`Id Device "${deviceIDBaru}" sudah terdaftar!`);
          setTimeout(() => setToastError(false), 5000);
          return;
        }

        const kelasDuplikat = Object.values(deviceMap).some(
          k => k.toLowerCase() === kelasBaru.toLowerCase()
        );
        if (kelasDuplikat) {
          setToastError(`Nama kelas "${kelasBaru}" sudah digunakan.`);
          setTimeout(() => setToastError(false), 5000);
          return;
        }

        // simpan mapping baru
        await set(ref(db, "deviceMapping/" + deviceIDBaru), kelasBaru);

        // update kelas di deviceStatus jika ada
        const statusSnap = await get(ref(db, "deviceStatus/" + deviceIDBaru));
        if (statusSnap.exists()) {
          await set(ref(db, "deviceStatus/" + deviceIDBaru), {
            ...statusSnap.val(),
            kelas: kelasBaru,
          });
        }
      } 
      // mode edit
      else {
        const deviceIDLama = pilihDeviceId;
        // hanya mengubah nama kelas (Device ID tetap)
        if (deviceIDBaru === deviceIDLama) {
          // update deviceMapping
          await set(ref(db, "deviceMapping/" + deviceIDLama), kelasBaru);

          // update deviceStatus
          const statusSnap = await get(ref(db, "deviceStatus/" + deviceIDLama));
          if (statusSnap.exists()) {
            await set(ref(db, "deviceStatus/" + deviceIDLama), {
              ...statusSnap.val(),
              kelas: kelasBaru,
            });
          }
        } 
        // mengubah Device ID (atau dgn nama kelas)
        else {
          if (deviceMap[deviceIDBaru]) {
            setToastError(`Id Device "${deviceIDBaru}" sudah digunakan!`);
            setTimeout(() => setToastError(false), 5000);
            return;
          }

          await remove(ref(db, "deviceMapping/" + deviceIDLama));
          // buat mapping baru
          await set(ref(db, "deviceMapping/" + deviceIDBaru), kelasBaru);

          // pindahkan / update deviceStatus
          const statusSnap = await get(ref(db, "deviceStatus/" + deviceIDLama));
          if (statusSnap.exists()) {
            const statusLama = statusSnap.val();
            await set(ref(db, "deviceStatus/" + deviceIDBaru), {
              ...statusLama,
              kelas: kelasBaru,
            });
            await remove(ref(db, "deviceStatus/" + deviceIDLama));
          } else {
            // Jika tidak ada status lama, buat baru
            await set(ref(db, "deviceStatus/" + deviceIDBaru), {
              kelas: kelasBaru,
            });
          }
        }
      }

      // Success
      setToastDK({ label: kelasBaru });
      setTimeout(() => setToastDK(false), 5000);
      // reset state
      setShowModal(false);
    } catch (err) {
      console.error(err);
      alert("Gagal menyimpan data. Silakan coba lagi.");
    }
  };

  // edit kelas&deviceId Desktop
  const handleEditIdD = (deviceId, kelas) => {
    setForm({ deviceId, namaKelas: kelas });
    setEditMode(true);
    setPilihDeviceId(deviceId); //simpan id lama
    setShowModal(true);
  };

  // edit kelas&deviceID Mobile
  const handleEditIdM = (deviceId, kelas) => {
    setForm({ deviceId, namaKelas: kelas });
    setEditMode(true);
    setPilihDeviceId(deviceId); //simpan id lama
    setShowModal(true);
  };

  // batal edit/tambah
  const handleBatal = () => {
    setEditMode(false);
    setPilihDeviceId(null);
    setForm({ namaKelas: "", deviceId: "" });
  };

  // buka modal hapus
  const handleHapus = (deviceId, kelas) => {
    setHapusData({ deviceId, kelas });
    setShowHapusModal(true);
  };

  // konfirmasi hapus
  const konfirmasiHapus = async () => {
    try {
      await remove(ref(db, "deviceMapping/" + hapusData.deviceId));

      // toast sukses
      setToastH({
        label: hapusData.kelas,
      });

      setTimeout(() => {
        setToastH(null);
      }, 5000);

      // tutup modal
      setShowHapusModal(false);
      setHapusData(null);

    } catch (err) {
      console.error(err);
      alert("Gagal menghapus data");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 animate-fadeIn">
        <h2 className="text-xl sm:text-2xl font-semibold mb-6 text-gray-700">
          Konfigurasi Sistem
        </h2>

        {/* Petunjuk/Disclaimer kelas & id device*/}
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-xl p-4 shadow-sm">
          <div className="flex gap-3 items-start">
            <div className="text-xl">⚠️</div>
            <div className="text-sm text-gray-700 leading-relaxed">
              <p className="font-semibold text-yellow-800 mb-1">
                Perhatian sebelum simpan data
              </p>
              <p className="mt-1">
                Pastikan atau cek ulang data nama kelas dan id device yang dimasukkan itu sudah sesuai!!
              </p>
            </div>
          </div>
        </div>

        {/* DESKTOP */}
        <div className="hidden md:block mb-6">
          {/* Header */}
          <div className="flex justify-end mb-4">
            {/* BUTTON TAMBAH */}
            <button
              onClick={() => {
                setShowModal(true);
                setEditMode(false);
                setForm({ deviceId: "", namaKelas: "" });
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primaryDark text-white rounded-xl hover:bg-primary transition"
            >
              <PlusCircle size={18} />
              <span>Tambah Kelas</span>
            </button>
          </div>

          {/* TABLE */}
          <div className="overflow-x-auto bg-white shadow-md rounded-2xl p-4 sm:p-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b text-gray-700 text-center">
                  <th className="py-3 px-4">Daftar Kelas</th>
                  <th className="py-3 px-4">ID Device</th>
                  <th className="py-3 px-4">Status Device</th>
                  <th className="py-3 px-4">Aksi</th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(deviceMap)
                  .sort(([, a], [, b]) => {
                    const parseKelas = (nama) => {
                      const match = nama.match(/Kelas\s*(\d+)([A-Z]?)/i);

                      return {
                        nomor: match ? parseInt(match[1], 10) : 999,
                        huruf: match ? (match[2] || "") : "",
                      };
                    };

                    const kelasA = parseKelas(a);
                    const kelasB = parseKelas(b);

                    // urut nomor kelas
                    if (kelasA.nomor !== kelasB.nomor) {
                      return kelasA.nomor - kelasB.nomor;
                    }
                    // urut huruf
                    return kelasA.huruf.localeCompare(kelasB.huruf);
                  })  
                  .map(([deviceId, kelas]) => {
                    const status = deviceStatus[deviceId];
                    const active = status?.status === "active";
                    const lastSeen = status?.lastSeen;
                    
                    const formatLastSeen = (lastSeen) => {
                      if (!lastSeen) return "-";

                      const diff = Date.now() - lastSeen;
                      const menit = Math.floor(diff / (1000 * 60));
                      const jam = Math.floor(diff / (1000 * 60 * 60));
                      const hari = Math.floor(diff / (1000 * 60 * 60 * 24));

                      if (menit < 60) return `Terakhir aktif ${menit} menit yang lalu`;
                      if (jam < 24) return `Terakhir aktif ${jam} jam yang lalu`;

                      return `Terakhir aktif ${hari} hari yang lalu`;
                    };
                    return (
                      <tr
                        key={deviceId}
                        className="text-center border-b hover:bg-gray-50 transition"
                      >
                        <td className="py-4 px-4 font-semibold text-gray-800">
                          {kelas}
                        </td>

                        <td className="py-4 px-4 text-gray-600">
                          {deviceId}
                        </td>

                        <td className="py-4 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              active
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                              }`}
                            >
                              {active ? "Active" : "Trouble"}
                            </span>

                            {!active && lastSeen && (
                              <span className="mt-1 text-xs text-gray-500">
                                {formatLastSeen(lastSeen)}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex justify-center gap-2">
                            {/* EDIT */}
                            <button
                              onClick={() => handleEditIdD(deviceId, kelas)}
                              className="p-2 text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition"
                            >
                              <Edit size={18} />
                            </button>

                            {/* HAPUS */}
                            <button
                              onClick={() => handleHapus(deviceId, kelas)}
                              className="p-2 text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        </div>
  
        {/* MOBILE CARD */}
        <div className="mb-6 md:hidden space-y-3">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => {
                setShowModal(true);
                setEditMode(false);
                setForm({ deviceId: "", namaKelas: "" });
              }}
              className="mb-6 flex items-center gap-2 px-4 py-2 bg-primaryDark text-white rounded-xl hover:bg-primary transition"
            >
              <PlusCircle size={18} />
              <span>Tambah Kelas</span>
            </button>
          </div>

          <div className="overflow-x-auto bg-white shadow-md rounded-2xl p-4 sm:p-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b text-gray-700 text-center">
                  <th className="py-3 px-4">Daftar Kelas</th>
                  <th className="py-3 px-4">Id Device</th>
                  <th className="py-3 px-4">Status Device</th>
                  <th className="py-3 px-4">Aksi</th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(deviceMap)
                  .sort(([, a], [, b]) => {
                    const parseKelas = (nama) => {
                      const match = nama.match(/Kelas\s*(\d+)([A-Z]?)/i);

                      return {
                        nomor: match ? parseInt(match[1], 10) : 999,
                        huruf: match ? (match[2] || "") : "",
                      };
                    };

                    const kelasA = parseKelas(a);
                    const kelasB = parseKelas(b);

                    // urut nomor kelas
                    if (kelasA.nomor !== kelasB.nomor) {
                      return kelasA.nomor - kelasB.nomor;
                    }
                    // urut huruf
                    return kelasA.huruf.localeCompare(kelasB.huruf);
                  })  
                  .map(([deviceId, kelas]) => {
                    const status = deviceStatus[deviceId];
                    const active = status?.status === "active";
                    const lastSeen = status?.lastSeen;
                    
                    const formatLastSeen = (lastSeen) => {
                      if (!lastSeen) return "-";

                      const diff = Date.now() - lastSeen;
                      const menit = Math.floor(diff / (1000 * 60));
                      const jam = Math.floor(diff / (1000 * 60 * 60));
                      const hari = Math.floor(diff / (1000 * 60 * 60 * 24));

                      if (menit < 60) return `Terakhir aktif ${menit} menit yang lalu`;
                      if (jam < 24) return `Terakhir aktif ${jam} jam yang lalu`;

                      return `Terakhir aktif ${hari} hari yang lalu`;
                    };
                    return (
                      <tr
                        key={deviceId}
                        className="text-center border-b hover:bg-gray-50 transition"
                      >
                        <td className="py-4 px-4 font-semibold text-gray-800">
                          {kelas}
                        </td>

                        <td className="py-4 px-4 text-gray-600">
                          {deviceId}
                        </td>

                        <td className="py-4 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              active
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                              }`}
                            >
                              {active ? "Active" : "Trouble"}
                            </span>

                            {!active && lastSeen && (
                              <span className="mt-1 text-xs text-gray-500">
                                {formatLastSeen(lastSeen)}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex justify-center gap-2">
                            {/* EDIT */}
                            <button
                              onClick={() => handleEditIdD(deviceId, kelas)}
                              className="p-2 text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition"
                            >
                              <Edit size={18} />
                            </button>

                            {/* HAPUS */}
                            <button
                              onClick={() => handleHapus(deviceId, kelas)}
                              className="p-2 text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        </div>

        {/* MODAL FORM KELAS&IDDEVICE */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 animate-fadeIn">
            <div className="bg-white p-6 rounded-xl w-80 shadow-lg animate-slideUp">
              <h2 className="text-lg font-bold mb-4">
                {editMode ? "Edit Kelas" : "Tambah Kelas"}
              </h2>

              <input
                type="text"
                placeholder="Nama Kelas (contoh: Kelas 5A)"
                value={form.namaKelas}
                onChange={(e) =>
                  setForm({ ...form, namaKelas: e.target.value })
                }
                className="w-full mb-3 p-2 border rounded"
              />

              <input
                type="text"
                placeholder="ID Device"
                value={form.deviceId}
                onChange={(e) =>
                  setForm({ ...form, deviceId: e.target.value.toUpperCase(), })
                }
                className="w-full mb-4 p-2 border rounded"
              />

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 rounded-xl font-medium border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
                >
                  Batal
                </button>

                <button
                  onClick={handleSubmit}
                  // disabled={!adaPerubahan}
                  // title={!adaPerubahan ? "Belum ada perubahan" : ""}
                  // className={`flex-1 py-2.5 rounded-xl font-medium transition-all ${
                  //   adaPerubahan
                  //     ? "bg-primaryDark text-white hover:bg-primary cursor-pointer"
                  //     : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  // }`}
                  className="flex-1 py-2.5 rounded-xl font-medium transition-all bg-primaryDark text-white hover:bg-primary cursor-pointer"
                >
                  Simpan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL HAPUS */}
        {showHapusModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn p-4">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-6 animate-slideUp">
              {/* Title */}
              <h2 className="text-lg font-bold text-center text-gray-800 mb-2">
                Hapus Data?
              </h2>

              {/* Description */}
              <p className="text-sm text-gray-500 text-center leading-relaxed">
                Data {" "}
                <span className="font-semibold text-gray-700">
                  {hapusData?.kelas}
                </span>{" "}
                dengan ID Device{" "}
                <span className="font-semibold text-gray-700">
                  {hapusData?.deviceId}
                </span>{" "}
                akan dihapus permanen.
              </p>

              {/* Button */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowHapusModal(false);
                    setHapusData(null);
                  }}
                  className="flex-1 py-2.5 rounded-xl font-medium border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
                >
                  Batal
                </button>

                <button
                  onClick={konfirmasiHapus}
                  className="flex-1 py-2.5 rounded-xl font-medium bg-red-600 text-white hover:bg-red-700 transition"
                >
                  Hapus
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Petunjuk/Disclaimer edit ambang batas*/}
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-xl p-4 shadow-sm">
          <div className="flex gap-3 items-start">
            <div className="text-xl">⚠️</div>
            <div className="text-sm text-gray-700 leading-relaxed">
              <p className="font-semibold text-yellow-800 mb-1">
                Perhatian sebelum mengubah ambang batas
              </p>

              <p>
                Perubahan nilai ambang batas sensor <b>harus mempertimbangkan standar resmi</b> dari lembaga terkait seperti <b>SNI</b>, <b>Permenkes</b>, atau instansi kesehatan dan lingkungan hidup lainnya.
              </p>

              <p className="mt-1">
                Pastikan nilai yang digunakan tetap <b>sesuai dengan kondisi lingkungan di Indonesia</b> agar hasil monitoring tetap akurat dan aman.
              </p>

              <ul className="list-disc ml-5 mt-2 text-xs text-gray-600 space-y-1">
                <li>
                  Contoh: Permenkes No. 2 Tahun 2023 yang menetapkan nilai standar kelembapan ruangan yaitu 40% - 60%. Atau SNI 03-6197-2000 yang menetapkan nilai standar intensitas cahaya (lux) yaitu minimal 250 lux.
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* tabel config sensor */}
        <div className="hidden md:block bg-white shadow-md rounded-2xl p-4 sm:p-6 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b text-gray-700 text-center">
                <th className="py-3 px-4">Tipe Sensor</th>
                <th className="py-3 px-4">
                  <p>Ambang Batas</p>
                  <p>Tidak Nyaman</p>
                </th>
                <th className="py-3 px-4">
                  <p>Ambang Batas</p>
                  <p>Tidak Sehat</p>
                </th>
                <th className="py-3 px-4">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(SENSOR_KEYS).map(([key, {label}]) => {
                // ambil data dari ambangBatas
                const sensorConfig = ambangBatas[key] || {};

                return (
                  <tr
                    key={key}
                    className="border-b hover:bg-gray-50 transition-all"
                  >
                    <td className="py-3 px-4 font-medium capitalize">
                      {label}
                    </td>

                    {/* Kolom peringatan — render aman meski nilainya object */}
                    <td className="py-3 px-4 text-m text-gray-600 capitalize">
                      {renderRingkasanLevel(sensorConfig, "waspada")}
                    </td>

                    {/* Kolom kritis — render aman meski nilainya object */}
                    <td className="py-3 px-4 text-sm text-gray-600 capitalize">
                      {renderRingkasanLevel(sensorConfig, "bahaya")}
                    </td>

                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleEdit(key)}
                        className="px-3 py-1 text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 active:bg-blue-100 transition-colors"
                      >
                        <Edit size={18}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MOBILE: Card per sensor (di bawah md) */}
        <div className="flex flex-col gap-3 md:hidden">
          {Object.entries(SENSOR_KEYS).map(([key, {label}]) => {
            const sensorConfig = ambangBatas[key] || {};
            return (
              <div key={key} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium capitalize">{label}</span>
                  </div>
                </div>
 
                {/* Dua kolom: peringatan & kritis */}
                <div className="grid grid-cols-2 gap-2 mb-3 capitalize">
                  <div className="bg-yellow-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-yellow-700 mb-1.5">Ambang Batas Tidak Nyaman</p>
                    {renderRingkasanLevel(sensorConfig, "waspada")}
                  </div>
                  <div className="bg-red-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-red-700 mb-1.5">Ambang Batas Tidak Sehat</p>
                    {renderRingkasanLevel(sensorConfig, "bahaya")}
                  </div>
                </div>
 
                {/* Tombol edit full-width */}
                <button
                  onClick={() => handleEdit(key)}
                  className="w-full py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 active:bg-blue-100 transition-colors"
                >
                  <span>Edit</span>
                </button>
              </div>
            );
          })}
        </div>
        
        {/* Modal Edit — form input menyesuaikan struktur data {min,max} */}
        {pilihSensor && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm sm:max-w-md p-6 animate-slideUp max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-1">Edit Ambang Batas</h3>
              <p className="text-sm text-gray-600 font-semibold mb-4 capitalize">
                {SENSOR_KEYS[pilihSensor]?.icon}{" "}
                {SENSOR_KEYS[pilihSensor]?.label ?? pilihSensor.replace("_", " ")}
              </p>

              <div className="space-y-4">
                {/* Render semua field secara dinamis */}
                {Object.keys(editData).map((fieldKey) => renderEditField(fieldKey))}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-3 pt-2 border-t border-gray-100">
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setPilihSensor(null)}
                      className="px-6 py-2 rounded-xl font-medium border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
                    >
                      Batal
                    </button>
                    <button
                      onClick={handleSave}
                      // disabled={!adaPerubahan}
                      // title={!adaPerubahan ? "Belum ada perubahan" : ""}
                      // className={`px-6 py-2 rounded-xl font-medium text-sm transition-all ${
                      //   adaPerubahan
                      //     ? "bg-primaryDark text-white hover:bg-primary cursor-pointer"
                      //     : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      // }`}
                      className="px-6 py-2 rounded-xl font-medium text-sm transition-all bg-primaryDark text-white hover:bg-primary cursor-pointer"
                    >
                      Simpan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notifikasi Ambang Batas - Muncul dari kanan bawah dengan animasi slide-in, hilang otomatis */}
        <div className={`fixed top-20 right-6 z-50 flex items-center gap-3 bg-white border border-green-200 shadow-lg rounded-2xl px-5 py-3.5 transition-all duration-500 ease-out ${
            toastAB ? "opacity-100 translate-x-0" : "opacity-0 translate-x-16 pointer-events-none"
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">Perubahan disimpan</p>
            <p className="text-xs text-gray-500">Ambang batas <span className="font-medium">{toastAB?.label}</span> berhasil diperbarui</p>
          </div>
        </div>

        {/* Toast Notifikasi Tambah Kelas & Edit ─ Muncul dari kanan bawah dengan animasi slide-in, hilang otomatis */}
        <div className={`fixed top-20 right-6 z-50 flex items-center gap-3 bg-white border border-green-200 shadow-lg rounded-2xl px-5 py-3.5 transition-all duration-500 ease-out ${
            toastDK ? "opacity-100 translate-x-0" : "opacity-0 translate-x-16 pointer-events-none"
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">Berhasil disimpan</p>
            <p className="text-xs text-gray-500">Tambah atau Edit <span className="font-medium">{toastDK?.label}</span> berhasil diperbarui</p>
          </div>
        </div>

        {/* Toast Notifikasi Hapus ─ hilang otomatis */}
        <div className={`fixed top-20 right-6 z-50 flex items-center gap-3 bg-white border border-red-200 shadow-lg rounded-2xl px-5 py-3.5 transition-all duration-500 ease-out ${
            toastH
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-16 pointer-events-none"
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Trash2 size={16} className="text-red-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">
              Berhasil dihapus
            </p>
            <p className="text-xs text-gray-500">
              <span className="font-medium">
                {toastH?.label}
              </span>{" "}
              berhasil dihapus
            </p>
          </div>
        </div>

        {/* Toast Notifikasi Duplikasi Kelas/ID Device ─ hilang otomatis */}
        <div className={`fixed top-20 right-6 z-50 flex items-center gap-3 bg-white border border-red-200 shadow-lg rounded-2xl px-5 py-3.5 transition-all duration-500 ease-out ${
            toastError
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-16 pointer-events-none"
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <X size={16} className="text-red-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">
              Error
            </p>
            <p className="text-xs text-gray-500">
              <span className="font-medium">
                {toastError}
              </span>
            </p>
          </div>
        </div>
      </main>

      <style>{`
        .animate-fadeIn  { animation: fadeIn  0.4s ease-in-out; }
        .animate-slideUp { animation: slideUp 0.35s ease-out;   }
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(15px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>
    </div>
  );
}